/**
 * atomDescription - per-atom natural-language description.
 *
 * Ported from `@alchemiesolns/molecule-descriptions/src/atom/describeAtom.ts`
 * so that all atom/feature/molecule descriptions live behind a single entry
 * point in `svg-interactive` (`buildAltTextMapFromStructure`).
 *
 * Per-feature naming: neighbor labels resolve through an `AtomLabelResolver`
 * built from the same `SerializationResult` that drives the HTML accessibility
 * tree. The resolver returns either the atom's per-feature label (when a
 * feature context is provided and the atom is in that feature) or the
 * default-feature label (highest type priority). The renderer is *blind* to
 * atom-naming policy — it just passes the right `featureContextId` for the
 * description it's currently building.
 *
 * If a label cannot be resolved (defensive — should not occur for atoms in
 * `result.atoms`) the resolver falls back to a bare element name; the
 * downstream sentence still reads sensibly.
 *
 * @package @alchemiesolns/svg-interactive
 */

import type {
  AtomLabelResolver,
  FeatureType,
  SerializationResult,
  SerializedAtom,
  SerializedBond,
  SerializedFeature,
} from '@alchemiesolns/feature-extractor';

// ---------------------------------------------------------------------------
// Atom subject formatting (chemistry-shorthand "NH" / "OH" / "C" prefix)
// ---------------------------------------------------------------------------

const ORGANIC_OVERRIDES = new Set(['Pr', 'R', 'Ac', 'Cn', 'Ts']);
const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

function toSubscript(n: number): string {
  return String(n)
    .split('')
    .map(d => SUBSCRIPT_DIGITS[parseInt(d, 10)] ?? d)
    .join('');
}

export function chemicalAtomSymbol(symbol: string, hydrogenCount: number): string {
  if (symbol === 'C') return 'C';
  if (ORGANIC_OVERRIDES.has(symbol)) return symbol;
  if (hydrogenCount <= 0) return symbol;
  if (hydrogenCount === 1) return `${symbol}H`;
  return `${symbol}H${toSubscript(hydrogenCount)}`;
}

// ---------------------------------------------------------------------------
// Primary feature filtering for shared-atom treatment
// ---------------------------------------------------------------------------

/** Primary feature types eligible for shared-atom "In {feature}" blocks */
const PRIMARY_TYPES: Set<FeatureType> = new Set(['ring', 'chain', 'bridged_bicyclic', 'atom_cluster']);

function filterPrimaryFeatures(features: SerializedFeature[]): SerializedFeature[] {
  return features.filter(f => PRIMARY_TYPES.has(f.type));
}

// ---------------------------------------------------------------------------
// Label resolution helper
// ---------------------------------------------------------------------------

/**
 * Resolve a neighbor atom's display label via the per-feature resolver.
 * Strips any trailing charge suffix (charge is verbalized separately) and
 * lowercases for prose. Returns "atom-?" only on truly defensive failure.
 */
function resolveNeighborLabel(
  resolve: AtomLabelResolver,
  atomIndex: number,
  featureContextId?: string,
): string {
  const raw = resolve(atomIndex, featureContextId);
  if (!raw) return 'atom-?';
  return raw.replace(/\s*\d*[+-]\s*$/, '').toLowerCase();
}

// ---------------------------------------------------------------------------
// describeAtom
// ---------------------------------------------------------------------------

/**
 * Describe an atom in natural-language sentence form, suitable for screen
 * readers, navigation labels, debug UI, and LLM summarization input.
 *
 * Examples (from the PIPH-59 spec):
 *   "C, single bonded to carbon-1 and carbon-3"
 *   "NH, single bonded to carbon-2 and carbon-5"
 *   "C, bridgehead, with 2 lone pairs, single bonded to carbon-2, carbon-4, and carbon-6"
 *   "C, wedge bonded to oxygen-1 of alcohol, dash bonded to chlorine-1 of chloride, single bonded to carbon-1 and carbon-3"
 *   "O, with negative charge, single bonded to carbon-3"
 *   "C, also carbon-1 of 5-membered ring. In 6-membered ring, single bonded to carbon-2 and carbon-4 which is also shared. In 5-membered ring, single bonded to carbon-2 and carbon-5 which is also shared"
 *
 * Hydrogen-to-heavy-atom bonds are intentionally omitted from the bond list
 * (their information is folded into the atom subject for non-carbons via the
 * implicit-H suffix; carbon's implicit hydrogens are conventional and not shown).
 *
 * @param atom              The atom to describe (must come from `result.atoms`).
 * @param result            The full serialization result for cross-references.
 * @param resolve           Per-feature atom-label resolver bound to `result`.
 * @param featureContextId  Optional. If provided, scope the description to
 *                          this feature only — used by the ARIA tree where
 *                          the user reaches an atom via a specific feature.
 *                          When omitted, shared atoms get the full multi-sentence
 *                          form covering all of their feature memberships.
 */
export function describeAtom(
  atom: SerializedAtom,
  result: SerializationResult,
  resolve: AtomLabelResolver,
  featureContextId?: string,
): string {
  const subject = chemicalAtomSymbol(atom.symbol, atom.hydrogenCount);

  // Resolve all features the atom belongs to (in JSON order).
  const allFeatures = atom.featureIds
    .map(id => result.features.find(f => f.id === id))
    .filter((f): f is SerializedFeature => f !== undefined);

  if (allFeatures.length === 0) {
    return assembleSimpleSentence(subject, atom, result, undefined, resolve);
  }

  const primaryFeatures = filterPrimaryFeatures(allFeatures);

  if (primaryFeatures.length === 0) {
    return assembleSimpleSentence(subject, atom, result, allFeatures[0], resolve);
  }

  const isShared = atom.shared && primaryFeatures.length > 1;

  if (!isShared) {
    return assembleSimpleSentence(subject, atom, result, primaryFeatures[0], resolve);
  }

  if (featureContextId) {
    const contextFeature = primaryFeatures.find(f => f.id === featureContextId);
    if (!contextFeature) {
      return assembleSharedSentences(subject, atom, result, primaryFeatures, resolve);
    }
    const otherFeatures = primaryFeatures.filter(f => f.id !== featureContextId);
    return assembleScopedSharedSentence(
      subject,
      atom,
      result,
      contextFeature,
      otherFeatures,
      resolve,
    );
  }

  return assembleSharedSentences(subject, atom, result, primaryFeatures, resolve);
}

// ---------------------------------------------------------------------------
// Sentence assembly
// ---------------------------------------------------------------------------

function assembleSimpleSentence(
  subject: string,
  atom: SerializedAtom,
  result: SerializationResult,
  feature: SerializedFeature | undefined,
  resolve: AtomLabelResolver,
): string {
  const parts: string[] = [subject];

  const props = formatSpecialProperties(atom);
  if (props.length > 0) parts.push(...props);

  const bondPhrase = formatBondList(atom, result, feature, resolve);
  if (bondPhrase) parts.push(bondPhrase);

  return parts.join(', ');
}

function assembleSharedSentences(
  subject: string,
  atom: SerializedAtom,
  result: SerializationResult,
  features: SerializedFeature[],
  resolve: AtomLabelResolver,
): string {
  const headerParts: string[] = [subject];

  // The "also" clause references the SECOND primary feature (the first will be
  // covered by its own "In {feature}" block; this avoids redundancy). The
  // label is resolved IN the second feature's scope so the per-feature name
  // for that scope is used (e.g. "carbon-1 of 5-membered ring").
  if (features.length > 1) {
    const secondFeature = features[1];
    const label = resolveNeighborLabel(resolve, atom.index, secondFeature.id);
    headerParts.push(`also ${label} of ${secondFeature.name}`);
  }

  const props = formatSpecialProperties(atom);
  headerParts.push(...props);
  const header = headerParts.join(', ');

  const featureSentences = features.map(f => {
    const bondPhrase = formatBondList(atom, result, f, resolve, /*flagSharedNeighbors*/ true);
    return `In ${f.name}, ${bondPhrase ?? 'no further bonds'}`;
  });

  return [header, ...featureSentences].join('. ');
}

function assembleScopedSharedSentence(
  subject: string,
  atom: SerializedAtom,
  result: SerializationResult,
  contextFeature: SerializedFeature,
  otherFeatures: SerializedFeature[],
  resolve: AtomLabelResolver,
): string {
  const parts: string[] = [subject];

  // List each non-context primary feature using THAT feature's per-feature
  // label for the atom — so the same atom can read as "carbon-3" of one ring
  // and "carbon-1" of another fused ring.
  for (const f of otherFeatures) {
    const label = resolveNeighborLabel(resolve, atom.index, f.id);
    parts.push(`also ${label} of ${f.name}`);
  }

  const props = formatSpecialProperties(atom);
  parts.push(...props);

  const bondPhrase = formatBondList(atom, result, contextFeature, resolve, /*flagSharedNeighbors*/ true);
  if (bondPhrase) parts.push(bondPhrase);

  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// Special properties
// ---------------------------------------------------------------------------

/** Spec order: bridgehead → lone pairs → charge → radical. */
function formatSpecialProperties(atom: SerializedAtom): string[] {
  const out: string[] = [];
  if (atom.bridgehead) out.push('bridgehead');
  if (atom.lonePairs > 0) {
    out.push(`with ${atom.lonePairs} lone pair${atom.lonePairs > 1 ? 's' : ''}`);
  }
  if (atom.charge !== 0) {
    out.push(`with ${atom.charge > 0 ? 'positive' : 'negative'} charge`);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Bond list formatting
// ---------------------------------------------------------------------------

interface BondInfo {
  /** "triple" / "double" / "wedge" / "dash" / "single" */
  phrase: string;
  /** Lowercased neighbor label, e.g. "carbon-3" */
  neighbor: string;
  /** When the neighbor is in a different feature, its feature display name */
  ofFeature?: string;
  /** Whether the neighbor itself is a shared atom */
  neighborShared: boolean;
}

const PHRASE_PRIORITY: Record<string, number> = {
  triple: 0,
  double: 1,
  wedge: 2,
  dash: 3,
  single: 4,
};

function bondPhraseFor(bond: SerializedBond): string {
  if (bond.bondType === 'w') return 'wedge';
  if (bond.bondType === 'd') return 'dash';
  if (bond.order === 3) return 'triple';
  if (bond.order === 2) return 'double';
  return 'single';
}

function formatBondList(
  atom: SerializedAtom,
  result: SerializationResult,
  feature: SerializedFeature | undefined,
  resolve: AtomLabelResolver,
  flagSharedNeighbors = false,
): string | undefined {
  const bonds = collectBonds(atom, result, feature, resolve);
  if (bonds.length === 0) return undefined;

  bonds.sort((a, b) => phraseOrder(a.phrase) - phraseOrder(b.phrase));

  // Group consecutive bonds with the same (phrase, ofFeature).
  const groups: Array<{ phrase: string; ofFeature?: string; entries: BondInfo[] }> = [];
  for (const b of bonds) {
    const last = groups[groups.length - 1];
    if (last && last.phrase === b.phrase && last.ofFeature === b.ofFeature) {
      last.entries.push(b);
    } else {
      groups.push({ phrase: b.phrase, ofFeature: b.ofFeature, entries: [b] });
    }
  }

  return groups
    .map(g => {
      const neighborTexts = g.entries.map(e => {
        if (flagSharedNeighbors && e.neighborShared) {
          return `${e.neighbor} which is also shared`;
        }
        return e.neighbor;
      });
      const list = joinList(neighborTexts);
      const suffix = g.ofFeature ? ` of ${g.ofFeature}` : '';
      return `${g.phrase} bonded to ${list}${suffix}`;
    })
    .join(', ');
}

function phraseOrder(phrase: string): number {
  return PHRASE_PRIORITY[phrase] ?? 99;
}

/**
 * Walk an atom's bonds and produce per-bond info, scoped to a feature if given.
 *
 * - Hydrogen bonds (the other atom is H) are filtered out.
 * - Cross-feature bonds get an `ofFeature` suffix naming the neighbor's feature.
 * - Same-feature neighbors resolve via this feature's per-feature label.
 *   Cross-feature neighbors resolve via the neighbor's default-feature label
 *   (resolver call without a context).
 */
function collectBonds(
  atom: SerializedAtom,
  result: SerializationResult,
  feature: SerializedFeature | undefined,
  resolve: AtomLabelResolver,
): BondInfo[] {
  const out: BondInfo[] = [];

  for (const bondIndex of atom.bondIndices) {
    const bond = result.bonds[bondIndex];
    if (!bond) continue;

    const otherIndex = bond.atomIndices[0] === atom.index
      ? bond.atomIndices[1]
      : bond.atomIndices[0];
    const otherAtom = result.atoms.find(a => a.index === otherIndex);
    if (!otherAtom) continue;

    if (otherAtom.symbol === 'H') continue;

    const phrase = bondPhraseFor(bond);

    let neighbor: string | undefined;
    let ofFeature: string | undefined;

    if (feature) {
      const neighborInFeature = feature.atomIndices.includes(otherIndex);
      if (neighborInFeature) {
        // Same-feature neighbor — use this feature's per-feature label.
        neighbor = resolveNeighborLabel(resolve, otherIndex, feature.id);
      } else {
        // Neighbor is NOT in current feature.
        // If the neighbor and the current atom share a different primary
        // feature, skip — the bond will be collected when we process that
        // other feature's block (avoids duplicates in the multi-sentence form).
        const atomPrimaryIds = new Set(atom.featureIds);
        const neighborInSharedPrimary = filterPrimaryFeatures(result.features)
          .some(f => f.id !== feature.id &&
                     atomPrimaryIds.has(f.id) &&
                     otherAtom.featureIds.includes(f.id));

        if (neighborInSharedPrimary) continue;

        // Truly external neighbor — name it via its default feature and tag
        // the containing feature. Default-feature lookup matches the
        // neighbor's own primary subtree label (e.g. ketone-O reads "Oxygen"
        // when the ketone is its only carrying feature).
        const otherFeature = result.features.find(f =>
          otherAtom.featureIds.includes(f.id),
        );
        neighbor = resolveNeighborLabel(resolve, otherIndex);
        if (otherFeature) ofFeature = otherFeature.name;
      }
    } else {
      // No feature context — resolve the neighbor by default feature.
      const otherFeature = result.features.find(f =>
        otherAtom.featureIds.includes(f.id),
      );
      neighbor = resolveNeighborLabel(resolve, otherIndex);
      if (otherFeature) ofFeature = otherFeature.name;
    }

    if (!neighbor) continue;

    out.push({
      phrase,
      neighbor,
      ofFeature,
      neighborShared: otherAtom.shared,
    });
  }

  return out;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Oxford-comma join: "a", "a and b", "a, b, and c". */
function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}
